export const buildCompanyQualificationData = (company) => ({
	name: company?.fields?.Name || '',
	industry: company?.fields?.Industry || '',
	employees: company?.fields?.Employees || '',
	founded: company?.fields?.Founded || '',
	country: company?.fields?.Country || '',
	provinceOrState: company?.fields?.ProvinceOrState || '',
	address: company?.fields?.Address || '',
	revenue: company?.fields?.Revenue || company?.fields?.AnnualRevenue || '',
	profitable: company?.fields?.Profitable || ''
});

export const buildGrantQualificationData = (grant) => ({
	name: grant?.fields?.Name || '',
	eligibleIndustry: grant?.fields?.Eligible_Industry || '',
	eligibleRegion: grant?.fields?.Eligible_Region || '',
	eligibleEmployees: grant?.fields?.Eligible_Employees || '',
	eligibleFounded: grant?.fields?.Eligible_Founded || '',
	eligibleRevenue: grant?.fields?.Eligible_Revenue || '',
	eligibleProfitable: grant?.fields?.Eligible_Profitable || '',
	grantOrLoan: grant?.fields?.GrantOrLoan || '',
	openOrClosed: grant?.fields?.OpenOrClosed || '',
	eligibilityCriteria: grant?.fields?.Eligibility_Criteria || '',
	eligibleActivities: grant?.fields?.Eligible_Activities || '',
	eligibleApplicants: grant?.fields?.Eligible_Applicants || '',
	description: grant?.fields?.Description || grant?.fields?.Description_2 || '',
	grantTextBlob: grant?.fields?.GrantTextBlob?.value || grant?.fields?.GrantTextBlob || ''
});

export const extractQualificationStatus = (content) => {
	if (!content) return null;
	// Tolerate formatting drift: bold or plain, any casing. Check the longer
	// phrases first so "PARTIALLY QUALIFIED"/"NOT QUALIFIED" aren't shortened to "QUALIFIED".
	const patterns = [
		/\*\*\s*(NOT QUALIFIED|PARTIALLY QUALIFIED|QUALIFIED)\s*\*\*/i,
		/\b(NOT QUALIFIED|PARTIALLY QUALIFIED|QUALIFIED)\b/i,
	];
	for (const pattern of patterns) {
		const match = content.match(pattern);
		if (match) return match[1].toUpperCase();
	}
	return null;
};

export const extractQualificationScore = (content) => {
	if (!content) return null;
	// Tolerate formatting drift around the FIT SCORE line (bold, colon, "/100" optional),
	// then fall back to any explicit "N/100" anywhere in the content.
	const patterns = [
		/fit\s*score[^\d]{0,12}(\d{1,3})\s*(?:\/\s*100)?/i,
		/(\d{1,3})\s*\/\s*100\b/,
	];
	for (const pattern of patterns) {
		const match = content.match(pattern);
		if (match) {
			const parsed = parseInt(match[1], 10);
			if (!Number.isNaN(parsed)) return Math.min(100, Math.max(0, parsed));
		}
	}
	return null;
};

export const scoreFromQualificationStatus = (status) => {
	if (!status) return null;
	const normalized = status.toUpperCase();
	if (normalized === 'QUALIFIED') return 88;
	if (normalized === 'PARTIALLY QUALIFIED') return 62;
	if (normalized === 'NOT QUALIFIED') return 25;
	return null;
};

export const getEffectiveQualificationScore = (score, status) => {
	if (typeof score === 'number' && !Number.isNaN(score)) return score;
	return scoreFromQualificationStatus(status);
};

export const getQualificationDisplay = (score, status) => {
	const effectiveScore = getEffectiveQualificationScore(score, status);
	if (effectiveScore == null && !status) return null;

	const value = effectiveScore ?? 50;

	if (value >= 80) {
		return {
			label: 'Strong Fit',
			score: value,
			className: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20'
		};
	}
	if (value >= 50) {
		return {
			label: 'Partial Fit',
			score: value,
			className: 'bg-amber-50 text-amber-700 ring-amber-600/20'
		};
	}
	return {
		label: 'Low Fit',
		score: value,
		className: 'bg-gray-100 text-gray-600 ring-gray-500/20'
	};
};

export const getGrantRecordId = (grant) => grant?.id || grant?.recordId || null;

export const fetchSavedQualification = async (companyId, grantId) => {
	const params = new URLSearchParams({
		companyId: String(companyId),
		grantId: String(grantId)
	});

	const response = await fetch(`/api/checkQualification?${params.toString()}`);
	if (!response.ok) {
		throw new Error(`Failed to load qualification (HTTP ${response.status})`);
	}

	return response.json();
};

const BULK_QUALIFICATION_CHUNK_SIZE = 50;

export const fetchBulkQualificationStatus = async (companyId, grantIds) => {
	const uniqueGrantIds = [...new Set(grantIds.filter(Boolean).map(String))];
	if (!companyId || uniqueGrantIds.length === 0) {
		return {};
	}

	// Chunk client-side so we never build an oversized URL and never ask the
	// backend (and Airtable) to handle hundreds of ids in a single request.
	const merged = {};
	for (let i = 0; i < uniqueGrantIds.length; i += BULK_QUALIFICATION_CHUNK_SIZE) {
		const chunk = uniqueGrantIds.slice(i, i + BULK_QUALIFICATION_CHUNK_SIZE);
		const params = new URLSearchParams({
			companyId: String(companyId),
			grantIds: chunk.join(',')
		});

		const response = await fetch(`/api/checkQualification?${params.toString()}`);
		if (!response.ok) {
			throw new Error(`Failed to load bulk qualification (HTTP ${response.status})`);
		}

		const data = await response.json();
		if (!data.success) {
			throw new Error(data.error || 'Failed to load bulk qualification');
		}

		Object.assign(merged, data.results || {});
	}

	return merged;
};

export const runBulkQualificationChecks = async ({
	company,
	grants,
	userEmail = null,
	onProgress = null,
	onGrantComplete = null,
	shouldCancel = () => false,
	force = false
}) => {
	if (!company?.fields) {
		throw new Error('Company information is required to check qualification.');
	}

	const grantEntries = grants
		.map((grant) => ({ grant, grantId: getGrantRecordId(grant) }))
		.filter(({ grantId }) => Boolean(grantId));

	const grantIds = grantEntries.map(({ grantId }) => grantId);
	const existing = force ? {} : await fetchBulkQualificationStatus(company.id, grantIds);
	const unchecked = grantEntries.filter(({ grantId }) => !existing[grantId]?.found);

	const summary = {
		total: grantEntries.length,
		alreadyChecked: grantEntries.length - unchecked.length,
		toCheck: unchecked.length,
		completed: 0,
		failed: 0,
		results: {}
	};

	for (let index = 0; index < unchecked.length; index += 1) {
		if (shouldCancel()) {
			break;
		}

		const { grant, grantId } = unchecked[index];
		onProgress?.({
			current: index + 1,
			total: unchecked.length,
			grantId,
			grantName: grant?.fields?.Name || 'Grant'
		});

		try {
			const data = await runQualificationCheck({ company, grant, userEmail });
			const result = {
				qualificationStatus: data.qualificationStatus || null,
				qualificationScore: data.qualificationScore ?? null
			};
			summary.results[grantId] = result;
			summary.completed += 1;
			onGrantComplete?.(grantId, result);
		} catch (error) {
			summary.failed += 1;
			console.error(`Bulk qualification failed for grant ${grantId}:`, error);
		}
	}

	return summary;
};

export const runQualificationCheck = async ({ company, grant, userEmail = null }) => {
	if (!company?.fields) {
		throw new Error('Company information is required to check qualification.');
	}

	const companyData = buildCompanyQualificationData(company);
	const grantData = buildGrantQualificationData(grant);
	const companyId = company?.id || null;
	const grantId = getGrantRecordId(grant);

	const response = await fetch('/api/checkQualification', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify({
			companyData,
			grantData,
			companyId,
			grantId,
			userEmail
		})
	});

	const data = await response.json().catch(() => ({}));

	if (!response.ok) {
		throw new Error(data.details || data.error || `HTTP error! status: ${response.status}`);
	}

	if (!data.success || !data.result) {
		throw new Error(data.error || 'No response from qualification check');
	}

	const qualificationStatus = data.qualificationStatus || extractQualificationStatus(data.result);
	const qualificationScore = data.qualificationScore ?? extractQualificationScore(data.result);

	return {
		...data,
		qualificationStatus,
		qualificationScore
	};
};
